import style from "./style.scss";
import editorialStyle from "../../../components/editorial/style.scss";

import { Fragment } from "preact";
import Link, {
  Button,
  ButtonList,
  StickyButtons,
  ButtonDualGroup,
  ButtonGroup,
  affiliateMerchants,
} from "../../../components/link";
import AffiliateDisclosure from "../../../components/affiliate-disclosure";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import FixedTable from "../../../components/fixed-table";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import NoWrap from "../../../components/no-wrap";
import Tags from "../../../components/tags";
import BluettiCouponCode from "../components/coupon-code";
import ImageSet from "../../../components/image-set";
import Image from "../../../components/image";
import ReviewItem from "../../../components/review-item";
import Author from "../../../components/author";
import Accordion from "../../../components/accordion";
import YouTube from "../../../components/youtube";
import Warning from "../../../components/warning";
import SlugHeading from "../../../components/slug-heading";
import PhotoListItem from "../../../components/photo-list-item";
import SlugLink from "../../../components/slug-link";
import Questions from "../../../components/questions";
import namedRoutes from "../../../settings/routes";
import { lookupByUrl } from "../../../settings/route-data-lookup";

const headerInfo = lookupByUrl(namedRoutes.bluetti.altCharger.transit);

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      positionY={67}
      background="/images/backgrounds/bluetti-chargers-transit.webp?v=2"
      titlePrefix="BLUETTI"
      title="Alternator Charger"
      subTitle="Ford Transit Install Guide"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <p>
          This guide provides instructions for installing a{" "}
          <strong>BLUETTI Charger 2</strong> or{" "}
          <strong>BLUETTI Charger 1</strong> alternator charger in a{" "}
          <strong>Ford Transit</strong>. The Transit's electrical system has
          unique features (BMS, CCPs, potential dual alternators/batteries) that
          require careful consideration.
        </p>

        <p>This guide is based on:</p>
        <ul>
          <li>Direct consultation with a Ford Pro BBAS Services Engineer</li>
          <li>Ford Body and Equipment Mounting Manual (BEMM)</li>
          <li>BLUETTI product manuals and specifications</li>
          <li>
            My experience installing alternator chargers in a Ford Transit
          </li>
        </ul>

        <AffiliateDisclosure />

        <Accordion
          items={[
            {
              heading: "Table of Contents",
              content: (
                <Fragment>
                  <ol>
                    <li>
                      <SlugLink>Charger Models</SlugLink>
                    </li>
                    <li>
                      <SlugLink>Fuse Upgrade</SlugLink>
                    </li>
                    <li>
                      <SlugLink>Connection Options</SlugLink>
                    </li>
                    <li>
                      <SlugLink>Transit Electrical System</SlugLink>
                      <ol class="upper-alpha">
                        <li>
                          <SlugLink>Alternator</SlugLink>
                        </li>
                        <li>
                          <SlugLink>Vehicle Battery</SlugLink>
                          <ol class="lower-roman">
                            <li>
                              <SlugLink>
                                Battery Management System (BMS)
                              </SlugLink>
                            </li>
                          </ol>
                        </li>
                        <li>
                          <SlugLink>Customer Connection Point (CCP)</SlugLink>
                        </li>
                      </ol>
                    </li>

                    <li>
                      <SlugLink>Making Connections</SlugLink>
                      <ol class="upper-alpha">
                        <li>
                          <SlugLink>Connect to Battery Cables</SlugLink>
                        </li>
                        <li>
                          <SlugLink>Connect Positive to CCP1</SlugLink>
                        </li>
                        <li>
                          <SlugLink>Connect Positive to CCP2</SlugLink>
                        </li>
                        <li>
                          <SlugLink>Connect Negative to Ground</SlugLink>
                        </li>
                      </ol>
                    </li>
                    <li>
                      <SlugLink>Summary of Steps</SlugLink>
                    </li>
                  </ol>
                </Fragment>
              ),
            },
          ]}
        />
        <Warning prefix="Warning">
          You can get electrocuted by your vehicle's battery and/or CCP. This
          install requires basic electrical knowledge and tools. If you're not
          comfortable working with your vehicle's electrical system, consult a
          qualified professional (feel free to provide them with a link to this
          guide).
        </Warning>
      </Center>
      {/* <Center>
        <p>
          The Ford Transit is a commercial vehicle designed to be upfitted and
          as a result its electrical system contains features not in many
          consumer vehicles. It contains both a Battery Management System (BMS)
          and at least one Customer Connection Point (CCP). Some Transits
          contain two or three CCP connections, each which can have different
          specifications and features. Additionally, many Ford Transits come
          from the factory with dual alternators and/or dual batteries. This
          guide will walk you through selecting which connections are an option
          for your specific vehicle model, Alternator Charger model/cable, and
          power station.
        </p>
      </Center> */}
      <Center>
        <SlugHeading tag="h2">Charger Models</SlugHeading>
        <p>
          If you're still deciding on a model, I recommend Charger 2 with the
          optional D+ signal wire for Transit installs, since Charger 1 doesn’t
          support D+ and is less reliable with the Transit’s smart alternator.
          For more details, see my{" "}
          <Link href={namedRoutes.bluetti.charger2.review}>
            Charger 2 review
          </Link>
          .
        </p>

        <ul class="none">
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
              img="/images/products/bluetti/charger-2/front.webp"
              title="BLUETTI Charger 2"
              description={
                <Fragment>Charger, solar, reverse charging, hub</Fragment>
              }
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/bluetti-charger-1"
              img="/images/products/bluetti/charger-1/front.webp"
              title="BLUETTI Charger 1"
              description={<Fragment>Basic charger</Fragment>}
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
        </ul>

        <h4>My Reviews</h4>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.bluetti.charger2.review} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.bluetti.charger1.review} />
          </li>
        </ul>

        <h4>Power Station Compatibility</h4>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.bluetti.charger2.compatibility} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.bluetti.charger1.compatibility} />
          </li>
        </ul>

        <h3>Charger 2 D+ Signal Wire</h3>
        <p>
          The D+ signal wire is now available for purchase, however if needed
          you can make your own using a{" "}
          <Link
            affiliate="https://amzn.to/4bMxHyY"
            href="https://www.amazon.com/dp/B01F4F5BPY/"
          >
            5.08mm pitch pluggable terminal
          </Link>{" "}
          and some{" "}
          <Link
            affiliate="https://amzn.to/4tjbiAB"
            href="https://www.amazon.com/dp/B01LCEEEZY/"
          >
            18 AWG stranded wire
          </Link>
          .
        </p>

        <PhotoListItem
          affiliateData={affiliateMerchants.bluetti}
          href="https://www.bluettipower.com/products/charger-2-d-signal-wire"
          img="/images/products/bluetti/charger-2/d+-signal-wire.webp"
          title="D+ Signal Wire"
          description={
            <Fragment>Engine run detection (smart alternators)</Fragment>
          }
          buttonText="BLUETTI"
          buttonIcon="Bluetti"
        />

        <p>
          The Transit's ignition and engine run signals are accessed under the
          driver’s seat from the C33‑E vehicle interface connector (full
          instructions coming soon).
        </p>

        <h3>Setup Videos</h3>
        <Accordion
          headingsAs="h4"
          items={[
            {
              heading: "Charger 2 Official Setup Videos",
              content: (
                <Fragment>
                  <h3>Install Guide</h3>
                  <YouTube id="2ejGheZQv3Y" />

                  <h3>App Setup</h3>
                  <YouTube id="dIXDqDwbods" />
                </Fragment>
              ),
            },
            {
              heading: "Charger 1 Official Setup Videos",
              content: (
                <Fragment>
                  <h3>Install Guide</h3>
                  <YouTube id="GtGN9-JApOQ" />

                  <h3>App Setup</h3>
                  <YouTube id="GMas-q21N6s" />
                </Fragment>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Fuse Upgrade</SlugHeading>

        <p>
          If you’re installing <strong>Charger 2</strong>, the included fuse
          assembly will not fit inside the Transit's tight battery compartment
          or on the CCP terminals. You will need to purchase the following
          components instead:
        </p>

        <ul class="none">
          <li>
            <PhotoListItem
              affiliate="https://amzn.to/49fvCM3"
              href="https://www.amazon.com/dp/B0FNQ1FN6C/"
              img="/images/products/igreely-6awg-battery.webp"
              title="iGreely Automotive Battery Cable"
              titleSuffix="(only need red)"
              description={<Fragment>6 AWG - 1 ft, 5/16" Lugs</Fragment>}
              buttonText="Amazon"
              buttonIcon="Amazon"
            />
          </li>
          <li>
            <PhotoListItem
              affiliate="https://amzn.to/3Nb6RYQ"
              href="https://www.amazon.com/dp/B004ZJ0S2M/"
              img="/images/products/blue-sea-systems/5255-80a-midi.webp"
              title="Blue Sea Systems 5255"
              description={<Fragment>80 Amp MIDI/AMI Fuse</Fragment>}
              buttonText="Amazon"
              buttonIcon="Amazon"
            />
          </li>
          <li>
            <PhotoListItem
              affiliate="https://amzn.to/4nYJnD1"
              href="https://www.amazon.com/dp/B004T1VYLA"
              img="/images/products/blue-sea-systems/7720.webp"
              title="Blue Sea Systems 7720"
              description={<Fragment>MIDI Safety Fuse Block</Fragment>}
              buttonText="Amazon"
              buttonIcon="Amazon"
            />
          </li>
        </ul>

        <p>
          The Charger 2 positive cable connects to one side of the fuse block,
          while the red 6 AWG battery cable connects to the opposite side and
          continues to your selected positive connection point (battery or CCP).
          I will explain how to choose the correct connection point below.
        </p>

        <p>
          There are cheaper fuse blocks available, but they transfer cable
          tension directly into the fuse, increasing the chance of cracking it
          during installation. The high quality Blue Sea Systems fuse block
          isolates the fuse from cable strain by using separate posts for the
          conductors and independent screws for the fuse, resulting in a
          reliable and durable setup.
        </p>

        <p>
          The same setup will also work for <strong>Charger 1</strong>, although
          you can use a{" "}
          <Link
            affiliate="https://amzn.to/45zEOIK"
            href="https://www.amazon.com/dp/B004ZJ0RL4/"
          >
            70A fuse
          </Link>{" "}
          if you prefer.
        </p>

        <Warning prefix="Warning">
          Please seek professional advice if planning on using a fuse different
          than what BLUETTI recommends! I’m not an electrician or electrical
          engineer.
        </Warning>
        <Accordion
          headingsAs="h3"
          items={[
            {
              heading: "Calculating Fuse Size",
              content: (
                <Fragment>
                  <p>
                    According to BLUETTI, the max amps is 62.5A for Charger 2
                    and 50A for Charger 1:
                  </p>
                  <FixedTable>
                    <table>
                      <thead>
                        <tr>
                          <th class={editorialStyle.sticky} />
                          <th>Max Amps</th>
                          <th>Wire Gauge</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <th class={editorialStyle.sticky}>Charger 2</th>
                          <td>62.5A</td>
                          <td>6 AWG</td>
                        </tr>
                        <tr>
                          <th class={editorialStyle.sticky}>Charger 1</th>
                          <td>50A</td>
                          <td>6 AWG</td>
                        </tr>
                      </tbody>
                    </table>
                  </FixedTable>

                  <p>
                    Based on a{" "}
                    <Link
                      external
                      href="https://www.bluesea.com/support/articles/Circuit_Protection/1441/Part_2%3A_Select_a_Fuse_and_Fuse_Holder_For_Your_DC_Product_Installation"
                    >
                      guide from Blue Sea Systems
                    </Link>{" "}
                    and the specifications from BLUETTI, I calculated:
                  </p>

                  <FixedTable>
                    <table>
                      <thead>
                        <tr>
                          <th class={editorialStyle.sticky} />
                          <th>Location</th>
                          <th>
                            Charger 2
                            <br />
                            (6AWG)
                          </th>
                          <th>
                            Charger 1
                            <br />
                            (6AWG)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <th class={editorialStyle.sticky}>
                            Min Fuse Rating <sup>1</sup>
                          </th>
                          <th>--</th>
                          <td>78.1A</td>
                          <td>62.5A</td>
                        </tr>
                        <tr class={editorialStyle.rowBorder}>
                          <th class={editorialStyle.sticky} rowSpan={2}>
                            Max Fuse Rating <sup>2</sup>
                          </th>
                          <th>Hot Engine Bay</th>
                          <td>100A</td>
                          <td>100A</td>
                        </tr>
                        <tr>
                          <th>Inside</th>
                          <td>125A</td>
                          <td>125A</td>
                        </tr>
                      </tbody>
                    </table>
                  </FixedTable>
                  <p class="caption">
                    <strong>1)</strong> Minimum amp rating equals max product
                    amperage x 125%.{" "}
                    <NoWrap>
                      <strong>2)</strong> Assumes
                    </NoWrap>{" "}
                    "single wire outside engine room" for cable size.
                  </p>

                  <p>
                    Based on my calculation, I understand these to be the
                    correct fuse options:
                  </p>
                  <ul>
                    <li>
                      <strong>Charger 2:</strong>{" "}
                      <Link
                        affiliate="https://amzn.to/3Nb6RYQ"
                        href="https://www.amazon.com/dp/B004ZJ0S2M/"
                      >
                        80A
                      </Link>
                      ,{" "}
                      <Link
                        affiliate="https://amzn.to/40OlapM"
                        href="https://www.amazon.com/dp/B004ZJ0RTQ/"
                      >
                        100A
                      </Link>
                      ,{" "}
                      <Link
                        affiliate="https://amzn.to/47pSFm2"
                        href="https://www.amazon.com/dp/B004ZJ0V1U/"
                      >
                        125A
                      </Link>
                    </li>
                    <li>
                      <strong>Charger 1:</strong>{" "}
                      <Link
                        affiliate="https://amzn.to/45zEOIK"
                        href="https://www.amazon.com/dp/B004ZJ0RL4/"
                      >
                        70A
                      </Link>
                      ,{" "}
                      <Link
                        affiliate="https://amzn.to/3Nb6RYQ"
                        href="https://www.amazon.com/dp/B004ZJ0S2M/"
                      >
                        80A
                      </Link>
                      ,{" "}
                      <Link
                        affiliate="https://amzn.to/40OlapM"
                        href="https://www.amazon.com/dp/B004ZJ0RTQ/"
                      >
                        100A
                      </Link>
                      ,{" "}
                      <Link
                        affiliate="https://amzn.to/47pSFm2"
                        href="https://www.amazon.com/dp/B004ZJ0V1U/"
                      >
                        125A
                      </Link>
                    </li>
                  </ul>
                  <p>
                    If you fuse too high, you risk an electrical fire. If you
                    fuse too low, you risk the fuse blowing during normal
                    operating conditions. When in doubt, fuse lower to prevent a
                    fire.
                  </p>
                </Fragment>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Connection Options</SlugHeading>
        <p>
          Overview of the connection options supported by each Charger model:
        </p>
        <FixedTable>
          <table>
            <thead>
              <tr>
                <th class={editorialStyle.sticky} />
                <th colSpan={2}>Fuse Rating</th>
                <th colSpan={3}>Connection Options</th>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>Model</th>
                <th>Included Fuse</th>
                <th>Max Amps</th>
                <th>Battery</th>
                <th>CCP1</th>
                <th>CCP2</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Charger 2:</strong> Normal Charging
                </th>
                <td>75A</td>
                <td>62.5A</td>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
                <td>
                  <span aria-label="No" title="No">
                    ❌
                  </span>
                </td>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Charger 2:</strong> Reverse Charging
                </th>
                <td>75A</td>
                <td>62.5A</td>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
                <td>
                  <span aria-label="No" title="No">
                    ❌
                  </span>
                </td>
                <td>
                  <span aria-label="No" title="No">
                    ❌
                  </span>
                </td>
              </tr>

              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky} rowspan={3}>
                  <strong>Charger 1</strong>
                </th>
                <td>60A</td>
                <td>50A</td>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
                <td>
                  <span aria-label="Yes" title="Yes">
                    ✅
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </FixedTable>

        <FixedTable>
          <table>
            <thead>
              <tr>
                <th class={editorialStyle.sticky} />
                <th>Fused Amps</th>
                <th>
                  Ignition Controlled <sup>1</sup>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>CCP1</strong>
                </th>
                <td>60A</td>
                <td>No</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>CCP2</strong>
                </th>
                <td>175A</td>
                <td>Yes</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>3x CCP</strong>
                </th>
                <td>60A each</td>
                <td>No</td>
              </tr>
            </tbody>
          </table>
        </FixedTable>
        <p class="caption">
          <strong>1.</strong> CCP2’s ignition‑controlled behavior is not the
          same as the D+ ignition‑sensing signal for Charger 2. For reliable
          charger activation, the D+ cable is the better option.
        </p>
      </Center>

      <Center>
        <SlugHeading tag="h2">Transit Electrical System</SlugHeading>
        <p>
          For additional details on the Ford Transit’s electrical system, refer
          to the BEMM:
        </p>
        <Accordion
          headingsAs="h3"
          items={[
            {
              heading:
                "Download Body and Equipment Mounting Manual (BEMM) for your Transit",
              content: (
                <Fragment>
                  <p>
                    You can locate the BEMM specific to your Transit model
                    online at{" "}
                    <Link
                      external
                      href="https://www.fordpro.com/en-us/upfit/bbas/"
                    >
                      Ford Pro Body Builder Advisory Service (BBAS)
                    </Link>
                    . Some older models can be more difficult to locate.
                  </p>
                  <p>Here are some for quick reference (North America):</p>
                  <ul>
                    {/* <li>
                      <Link
                        external
                        href="https://api-prd.fordpro.com/pro/bbas/download/BBLB/67c1e1bdfa78ad3df02407a7"
                      >
                        2025
                      </Link>
                    </li> */}
                    <li>
                      <Link
                        external
                        href="https://api-prd.fordpro.com/pro/bbas/download/BBLB/66841026c7fa557b06a5d5d8"
                      >
                        2024
                      </Link>
                    </li>
                    <li>
                      <Link
                        external
                        href="https://api-prd.fordpro.com/pro/bbas/download/BBLB/6632290fec530f3fb24e9d28"
                      >
                        2023
                      </Link>
                    </li>
                    <li>
                      <Link
                        external
                        href="https://api-prd.fordpro.com/pro/bbas/download/BBLB/654dc61fbd2cfc7167ebce4f"
                      >
                        2022
                      </Link>
                    </li>

                    <li>
                      <Link
                        external
                        href="https://api-prd.fordpro.com/pro/bbas/download/BBLB/65f096cacab88342d475f33c"
                      >
                        2020-21
                      </Link>
                    </li>
                    <li>
                      <Link
                        external
                        href="https://api-prd.fordpro.com/pro/bbas/download/BBLB/654dc61fbd2cfc7167ebce14"
                      >
                        2020
                      </Link>
                    </li>
                  </ul>
                </Fragment>
              ),
            },
          ]}
        />

        <div class={style.section}>
          <SlugHeading tag="h3">Alternator</SlugHeading>
          <p>
            The current Transit now comes standard with a robust{" "}
            <strong>250A alternator</strong>. These extra amps are ideal for
            charging a power station without putting too much strain on your
            vehicle's alternator. Additionally, some Transits come equipped with
            dual 250A alternators. It’s best to check what your vehicle has
            before pulling the full 800W from Charger 2. Most alternators have a
            sticker with their specifications; however, this can sometimes be
            difficult to see once installed (I can spot mine if I lay under my
            van at just the right angle). Your local Ford dealer should be able
            to help you determine which alternator is installed in your van.
          </p>
          <p>
            Recent Transits come with <strong>smart alternators</strong> that
            adjust voltage output based on driving conditions to improve fuel
            efficiency. While this sounds great, it can cause the alternator
            charger to stop charging while driving. Charger 2 supports an
            optional D+ cable (sold separately) that detects the ignition state
            directly, avoiding the inconsistencies of voltage‑based detection.
          </p>
        </div>

        <div class={style.section}>
          <SlugHeading tag="h3">Vehicle Battery</SlugHeading>
          <p>
            Unlike most vehicles, the Ford Transit's battery/batteries are
            located inside the cabin under the driver's seat. This is important
            for planning your wire routing.
          </p>
          <p>
            The Transit has 3 battery configurations (each uses a size H6
            battery):
          </p>
          <ol class="upper-alpha">
            <li>
              <strong>Single Battery:</strong> 610CCA
            </li>
            <li>
              <strong>Single AGM Battery:</strong> 760CCA
            </li>
            <li>
              <strong>Dual AGM Batteries:</strong> 760CCA each
            </li>
          </ol>

          <ImageSet
            size={3}
            border={true}
            showAlt
            images={[
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/battery-config-a.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/battery-config-a.webp",
                alt: "A. Single Battery",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/battery-config-b.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/battery-config-b.webp",
                alt: "A. Single AGM Battery",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/battery-config-c.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/battery-config-c.webp",
                alt: "A. Dual AGM Batteries",
              },
            ]}
          />
          <p>
            Two batteries can make for a tighter install of the fuse block when
            choosing to connect to the battery instead of the CCP, but it is
            still manageable. Dual batteries can act as a bit of an extra buffer
            to the alternator and also mean you don't need to worry as much if
            you accidentally leave your headlights on.
          </p>

          <SlugHeading tag="h4">Battery Management System (BMS)</SlugHeading>
          <p>
            The Battery Management System (BMS) is responsible for monitoring
            and managing the battery's condition, charging rate, and power
            distribution. It ensures that the battery maintains an optimal
            charge while preventing excessive drain from vehicle accessories.
            When connecting directly to the battery cables, it is important not
            to bypass the BMS sensors.
          </p>
        </div>

        <div class={style.section}>
          <SlugHeading tag="h3">Customer Connection Point (CCP)</SlugHeading>

          <p>
            To determine which Customer Connection Points (CCPs) are installed
            in your Transit, remove the CCP cover (located on the outside edge
            of your driver's seat):
          </p>
          <ul>
            <li>
              <strong>One Stud:</strong> only CCP1 is present.
            </li>
            <li>
              <strong>Two Studs:</strong> CCP1 and CCP2 are present.
            </li>
            <li>
              <strong>Three Studs:</strong> Three CCP connections are present
              (each with the same spec as CCP1, follow guide for CCP1).
            </li>
          </ul>

          <ImageSet
            size={3}
            border={true}
            showAlt
            images={[
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/ccp-1-only.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/ccp-1-only.webp",

                alt: "CCP1",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/ccp.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/ccp.webp",

                alt: "CCP1 and CCP2",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/ccp-3x.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/ccp-3x.webp",

                alt: "3x CCP",
              },
            ]}
          />

          <p style="clear: both;" />

          <FixedTable>
            <table>
              <thead>
                <tr>
                  <th class={editorialStyle.sticky} />
                  <th>Fused Amps</th>
                  <th>Ignition Controlled</th>
                  <th>Torque</th>
                  <th>Nut</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>CCP1</strong>
                  </th>
                  <td>60A</td>
                  <td>No</td>
                  <td>
                    <NoWrap>2.6-3.6 ft·lb</NoWrap> <NoWrap>(3.5-4.5Nm)</NoWrap>
                  </td>
                  <td>M5</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>CCP2</strong>
                  </th>
                  <td>175A</td>
                  <td>Yes</td>
                  <td>
                    <NoWrap>8.85 ft·lb</NoWrap> <NoWrap>(12Nm)</NoWrap>
                  </td>
                  <td>M8</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>3x CCP</strong>
                  </th>
                  <td>60A each</td>
                  <td>No</td>
                  <td>
                    <NoWrap>2.6-3.6 ft·lb</NoWrap> <NoWrap>(3.5-4.5Nm)</NoWrap>
                  </td>
                  <td>M5</td>
                </tr>
              </tbody>
            </table>
          </FixedTable>

          <Warning prefix="Caution">
            Use caution when removing the CCP cover as the seat base can ground
            to the battery. When connecting to the CCP, it is recommended to
            disconnect the battery ground so as to avoid a short circuit.
          </Warning>
        </div>
      </Center>
      <Center>
        <SlugHeading tag="h2">Making Connections</SlugHeading>
        <p>
          You will either need to connect to the Transit's battery cables or to
          CCP1 or CCP2.
        </p>

        <div class={style.section}>
          <SlugHeading tag="h3">Connect to Battery Cables</SlugHeading>
          <p>
            <strong>Recommend:</strong> This connection works with both models
            and has the least restrictions. It was recommended to me by a Ford
            Pro BBAS Services Engineer after I explained a similar alternator
            charger specifications and functions.
          </p>
          <p>
            Here is how my alternator charger is connected. Note I also upgraded
            the fuse block:
          </p>
          <ImageSet
            shadow={true}
            showAlt
            images={[
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20240720_221759375.jpg",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20240720_221759375.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20240720_221759375.webp",
                alt: "Ford Transit dual batteries with alternator charger cables and Blue Sea fuse block",
              },
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20240720_221759375_install.png",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20240720_221759375_install.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20240720_221759375_install.webp",
                alt: "Highlighted connections for Ford Transit dual batteries with alternator charger cables and Blue Sea fuse block",
              },
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20240720_223337589.jpg",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20240720_223337589.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20240720_223337589.webp",
                alt: "Ford Transit dual batteries with alternator charger cables",
              },
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20240720_223337589_install.png",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20240720_223337589_install.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20240720_223337589_install.webp",
                alt: "Highlighted connections for Ford Transit dual batteries with alternator charger",
              },
            ]}
          />

          <p>
            Here are the connection points for each of the three battery
            configurations:
          </p>
          <ImageSet
            size={3}
            border={true}
            showAlt
            images={[
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/battery-config-a.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/battery-config-a.webp",
                alt: "A. Single Battery",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/battery-config-b.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/battery-config-b.webp",
                alt: "A. Single AGM Battery",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/battery-config-c.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/battery-config-c.webp",
                alt: "A. Dual AGM Batteries",
              },
            ]}
          />

          <Accordion
            headingsAs="h4"
            items={[
              {
                heading: "Accessing the Batteries",
                content: (
                  <Fragment>
                    <p>
                      Watch the following video by Ford on how to access the
                      battery cables. If needed, you can remove the driver's
                      seat (instructions below).
                    </p>
                    <YouTube id="xglmBiyTifQ" time="42" />

                    <p>
                      The battery cables should be fixed to the terminal
                      positive and negative posts with a torque of 8.0 Nm ± 1.2
                      Nm.
                    </p>
                  </Fragment>
                ),
              },
              {
                heading: "Removing the Driver's Seat",
                content: (
                  <Fragment>
                    <p>
                      Removing the driver's seat may be necessary, depending on
                      how you plan to route the cables and mount the fuse block.
                      Here is a video demonstrating the process, though it may
                      vary slightly depending on your seat setup. While the
                      video includes seat belt removal, this step isn’t
                      required—you can simply set the seat aside, either inside
                      or outside the van.
                    </p>
                    <YouTube id="8GWCQiwQZ2g" time="105" />
                  </Fragment>
                ),
              },
            ]}
          />
        </div>

        <div class={style.section}>
          <SlugHeading tag="h3">Connect Positive to CCP1</SlugHeading>
          <p>
            <strong>Only supported for Charger 1:</strong> Since CCP1 is fused
            at 60A, it doesn’t support the 62.5A maximum charge rating of
            Charger 2 model (Charger 1 maximum charge rating is 50A). CCP1 could
            support reverse charge and battery maintenance features since it
            isn't ignition controlled. Uses an M5 nut torqued to{" "}
            <NoWrap>2.6-3.6 ft·lb</NoWrap> <NoWrap>(3.5-4.5Nm)</NoWrap>.
          </p>
          <ImageSet
            shadow={true}
            showAlt
            images={[
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20250514_020942584.jpg",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20250514_020942584.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20250514_020942584.webp",
                alt: "Ford Transit CCP1 with an alternator charger cable",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/ccp-1.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/ccp-1.webp",
                border: true,
                alt: "CCP1",
              },
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20250514_021611049.jpg",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20250514_021611049.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20250514_021611049.webp",
                alt: "Ford Transit CCP with cover",
              },
            ]}
          />
          <Warning prefix="Caution">
            Use caution when removing the CCP cover as the seat base can ground
            to the battery. When connecting to the CCP, it is recommended to
            disconnect the battery ground so as to avoid a short circuit.
          </Warning>
        </div>

        <div class={style.section}>
          <SlugHeading tag="h3">Connect Positive to CCP2</SlugHeading>
          <p>
            <strong>Supported for either model:</strong> Doesn't fully support
            reverse charge or battery maintenance features. CCP2 is ignition
            controlled, meaning it only provides power when the van is running
            (with an automatic shutoff delay). This prevents accidental battery
            drain but also means it won't provide charging when the van is off.
            Because CCP2 is fused at 175A it supports the maximum charge ratings
            of both models (50A and 62.5A). Uses an M8 nut torqued to{" "}
            <NoWrap>8.85 ft·lb</NoWrap> <NoWrap>(12Nm)</NoWrap>.
          </p>
          <ImageSet
            shadow={true}
            showAlt
            images={[
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20250514_021243690.jpg",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20250514_021243690.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20250514_021243690.webp",
                alt: "Ford Transit CCP2 with 8AWG cable",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/ccp-2.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/ccp-2.webp",
                border: true,
                alt: "CCP2",
              },
            ]}
          />
          <Warning prefix="Caution">
            Use caution when removing the CCP cover as the seat base can ground
            to the battery. When connecting to the CCP, it is recommended to
            disconnect the battery ground so as to avoid a short circuit.
          </Warning>
        </div>

        <div class={style.section}>
          <SlugHeading tag="h3">Connect Negative to Ground</SlugHeading>
          <p>
            You will be connecting the charger's negative cable to either the
            battery's negative cable or to a chassis ground point. The following
            images depict the official chassis ground points and the negative
            terminal connection recommended by a Ford Pro BBAS Services Engineer
            (2024 model year). Ground point <strong>"N"</strong> is a specific
            point on the van's negative battery cable. Ground points{" "}
            <strong>#78</strong> and <strong>#79</strong> are also close by but
            I found they are not easy to access (the floor panels in this spot
            are difficult to remove). Ideally, Ford would have included a
            dedicated ground point under the CCP cover. If none of the existing
            ground points are suitable, you can add a new one by following the
            guidelines outlined in the 2024 BEMM (see{" "}
            <em>Adding a New Ground Point</em> below).
          </p>
          <ImageSet
            shadow={true}
            showAlt
            images={[
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/ground-points-2024-gas.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/ground-points-2024-gas.webp",
                border: true,
                alt: "2024 Ford Transit ground points and negative terminal",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/ground-n.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/ground-n.webp",
                border: true,
                alt: "2024 Ford Transit negative terminal",
              },
              {
                max: "/images/r/ecoflow-alternator-chargers/max/20240719_202430339.jpg",
                lg: "/images/r/ecoflow-alternator-chargers/lg/20240719_202430339.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/20240719_202430339.webp",
                alt: "Photo of Ford Transit negative terminal connection",
              },
              {
                lg: "/images/r/ecoflow-alternator-chargers/lg/ground-79-78.webp",
                sm: "/images/r/ecoflow-alternator-chargers/sm/ground-79-78.webp",
                border: true,
                alt: "2024 Ford Transit ground points #78-79",
              },
            ]}
          />
          <Accordion
            headingsAs="h4"
            items={[
              {
                heading: "Accessing the Batteries",
                content: (
                  <Fragment>
                    <p>
                      Watch the following video by Ford on how to access the
                      battery cables. If needed, you can remove the driver's
                      seat (instructions below).
                    </p>
                    <YouTube id="xglmBiyTifQ" time="42" />
                  </Fragment>
                ),
              },
              {
                heading: "Removing the Driver's Seat",
                content: (
                  <Fragment>
                    <p>
                      Removing the driver's seat may be necessary, depending on
                      how you plan to route the cables and mount the fuse block.
                      Here is a video demonstrating the process, though it may
                      vary slightly depending on your seat setup. While the
                      video includes seat belt removal, this step isn’t
                      required—you can simply set the seat aside, either inside
                      or outside the van.
                    </p>
                    <YouTube id="8GWCQiwQZ2g" time="105" />
                  </Fragment>
                ),
              },
              {
                heading: "Adding a New Ground Point",
                content: (
                  <Fragment>
                    <p>
                      If an existing official ground point is unsuitable, you
                      can add a new one by following the instructions in the{" "}
                      <Link
                        external
                        href="https://api-prd.fordpro.com/pro/bbas/download/BBLB/66841026c7fa557b06a5d5d8"
                      >
                        2024 BEMM
                      </Link>
                      :
                    </p>
                    <blockquote>
                      <strong>4.2.5 Grounding</strong>
                      <br />
                      <p>
                        If a new grounding point is required, avoid placing it
                        in a wet area, especially for high current grounds.
                        Ground connections should be routed back close to the
                        location of the +12V supply. This helps to reduce the
                        electromagnetic field particularly generated by inrush
                        current and improve electromagnetic compatibility.
                      </p>
                      <p>
                        Drill point screws are not to be used for any ground
                        attachments:
                      </p>
                      <ul>
                        <li>
                          Do not ground to moving structures, for example:
                          doors, deck lids, lift gates, as the ground return
                          path through the hinges is not reliable
                        </li>
                        <li>
                          Do not place more than 2 eyelet terminals under a
                          single ground screw Refer to: 4.5 Battery and Cables
                        </li>
                        <li>
                          Do not place electrical component attachments or
                          ground nuts adjacent to vehicle fuel tanks or fuel
                          lines
                        </li>
                        <li>
                          Each individual third party load must have a dedicated
                          ground connected directly to the vehicle body ground
                          or to the battery negative spare stud - see
                          ‘Additional Loads and Charging Systems’ later in this
                          section of the BEMM
                        </li>
                        <li>
                          Multiple grounds must not go to an interim splice to a
                          main ground cable
                        </li>
                        <li>
                          If additional ground studs are required it is
                          recommended to install a busbar with multiple studs
                          fed directly from the battery ground post. See
                          ‘Additional Loads and Charging Systems’
                        </li>
                      </ul>
                    </blockquote>
                    <p class="caption">
                      Thanks to FarOutRide for bringing this to my attention on
                      Facebook.
                    </p>
                  </Fragment>
                ),
              },
            ]}
          />
        </div>
      </Center>
      <Center>
        <SlugHeading tag="h2">Summary of Steps</SlugHeading>
        <p>
          Before beginning the final cable routing, it's a good idea to dry-fit
          the components to ensure everything works as expected. This allows you
          to test connections and verify functionality before committing to
          permanent installation.
        </p>
        <ol>
          <li>
            <strong>Route cables:</strong> Depending on your build, cables may
            need to be routed under the floor, through walls, or behind
            cabinets. Completing this step first is often best, as the end that
            connects to the van is the smallest. This makes it easier to pull
            through tight spaces where the larger end may not fit.
          </li>
          <li>
            <strong>Mount Charger:</strong> Attach it securely using screws or
            bolts. If needed, it can temporarily rest on the floor during setup.
          </li>
          <li>
            <strong>Connect Cables to Charger:</strong> Connect input and output
            cables to the charger using the instructions in the manual (manuals:{" "}
            <Link
              external
              href="https://cdn.shopify.com/s/files/1/0550/5620/3836/files/charger-2-user-manual.pdf?v=1767706542"
            >
              Charger 2
            </Link>
            ,{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://s4.bluettipower.com/bluetti_lgf/support/2025/05/4a4f1b84-aa5a-4563-8e3f-28f5c3053576.pdf"
            >
              Charger 1
            </Link>
            ).
          </li>
          <li>
            <strong>Determine Fuse/Breaker:</strong> If needed, refer to the{" "}
            <SlugLink>Fuse Upgrade</SlugLink> section above to select the
            appropriate fuse or breaker for your installation.
          </li>
          <li>
            <strong>Connect Input Cable to Van's Electrical System:</strong>{" "}
            Securely connect the positive cable to either the battery cable,
            CCP1, or CCP2. Connect the negative cable to either the battery
            cable or a suitable ground point.
          </li>
          <li>
            <strong>Press Power Button on Alternator Charger</strong> (Charger 2
            only): Locate the small button on the front of the charger and press
            it to power the unit on.
          </li>
          <li>
            <strong>Set Start Voltage in BLUETTI App:</strong> Adjust this
            setting to ensure the alternator charger automatically starts and
            stops based on whether the vehicle is on or off.
          </li>
          <li>
            <strong>Connect D+ Cable:</strong> If you’re using the optional D+
            cable, connect it according to the instructions in the BLUETTI
            manual. This allows the charger to read the ignition signal directly
            instead of relying on voltage detection.
          </li>
          <li>
            <strong>Connecting Power Station:</strong> Follow the instructions
            in the app for setting up the connection to the power station.
          </li>
        </ol>

        <BluettiCouponCode
          href="https://www.bluettipower.com/"
          buttonText="BLUETTI Store"
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Where to Buy</SlugHeading>

        <ButtonDualGroup>
          <ButtonGroup title="BLUETTI Charger 2">
            <ButtonList>
              <Button
                affiliateData={affiliateMerchants.bluetti}
                href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
                icon="Bluetti"
              >
                Buy from BLUETTI
              </Button>
              <Button
                affiliate="https://amzn.to/4q6J9LD"
                href="https://www.amazon.com/dp/B0FS7C11QG/"
                secondary
                icon="Amazon"
              >
                Buy on Amazon
              </Button>
            </ButtonList>
          </ButtonGroup>
          <ButtonGroup title="BLUETTI Charger 1">
            <ButtonList>
              <Button
                affiliateData={affiliateMerchants.bluetti}
                href="https://www.bluettipower.com/products/bluetti-charger-1"
                icon="Bluetti"
              >
                Buy from BLUETTI
              </Button>
              <Button
                affiliate="https://amzn.to/4svZ7AI"
                href="https://www.amazon.com/dp/B0DGXYPMQ4/"
                secondary
                icon="Amazon"
              >
                Buy on Amazon
              </Button>
            </ButtonList>
          </ButtonGroup>
        </ButtonDualGroup>

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>
      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.bluetti.vanlife} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.bluetti.charger2.review} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.alternatorChargers} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.inventory.vanlife} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
      <StickyButtons
        title="BLUETTI Alternator Chargers"
        description="Charger 2 & Charger 1"
        options={[
          {
            text: "View on BLUETTI",
            icon: "Bluetti",
            options: [
              {
                text: "BLUETTI Charger 2",
                affiliateData: affiliateMerchants.bluetti,
                href: "https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger",
              },
              {
                text: "BLUETTI Charger 1",
                affiliateData: affiliateMerchants.bluetti,
                href: "https://www.bluettipower.com/products/bluetti-charger-1",
              },
            ],
          },
          {
            text: "View on Amazon",
            icon: "Amazon",
            secondary: true,
            options: [
              {
                text: "BLUETTI Charger 2",
                affiliate: "https://amzn.to/4q6J9LD",
                href: "https://www.amazon.com/dp/B0FS7C11QG/",
              },
              {
                text: "BLUETTI Charger 1",
                affiliate: "https://amzn.to/4svZ7AI",
                href: "https://www.amazon.com/dp/B0DGXYPMQ4/",
              },
            ],
          },
        ]}
      />
    </Editorial>
  </div>
);

export default Page;
